import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient, ApiError, type TeamDraft } from '../lib/api';

const SAVE_DEBOUNCE_MS = 600;

export function TeamDraftEdit() {
  const params = useParams();
  const navigate = useNavigate();
  const orgId = decodeURIComponent(params.orgId ?? '');
  const draftId = decodeURIComponent(params.draftId ?? '');

  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.orgs.drafts
      .get(orgId, draftId)
      .then((d) => {
        if (cancelled) return;
        setDraft(d);
        setBody(d.body);
        setNote(d.note ?? '');
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Failed to load draft');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, draftId]);

  useEffect(() => {
    if (!draft) return;
    if (body === draft.body && (note ?? '') === (draft.note ?? '')) return;
    setSaving('saving');
    const handle = window.setTimeout(async () => {
      try {
        const updated = await apiClient.orgs.drafts.patch(orgId, draftId, { body, note });
        setDraft(updated);
        setSaving('saved');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Auto-save failed');
        setSaving('idle');
      }
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [body, note, draft, orgId, draftId]);

  const handleSubmit = async () => {
    if (!draft) return;
    setSubmitting(true);
    setError(null);
    try {
      // Flush any pending edits first.
      await apiClient.orgs.drafts.patch(orgId, draftId, { body, note });
      const submitted = await apiClient.orgs.drafts.submit(orgId, draftId, { note });
      setDraft(submitted);
      navigate(`/orgs/${encodeURIComponent(orgId)}/skills/${encodeURIComponent(draft.skillId)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft && !error)
    return (
      <main className="flex h-full items-center justify-center text-[12px] text-text-subtle">
        Loading draft…
      </main>
    );

  if (!draft)
    return (
      <main className="flex h-full items-center justify-center text-[12px] text-[var(--color-danger)]">
        {error}
      </main>
    );

  const readOnly = draft.state !== 'draft' && draft.state !== 'sent_back';

  return (
    <div className="flex h-full flex-col">
      <header className="hairline-b flex items-center justify-between gap-3 px-4 py-2">
        <div>
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/skills/${encodeURIComponent(draft.skillId)}`}
            className="text-[12px] text-text-muted hover:text-[var(--color-accent)]"
          >
            ← Back to team skill
          </Link>
          <h1 className="mt-1 font-mono text-[13px]">
            {readOnly ? 'Your submitted draft' : 'Edit your suggestion'}
          </h1>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-text-subtle">
          <span>State: {draft.state.replace('_', ' ')}</span>
          {!readOnly && (
            <span>
              {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : ''}
            </span>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="btn btn-primary"
            >
              {submitting ? 'Submitting…' : 'Submit for review'}
            </button>
          )}
        </div>
      </header>
      {error && (
        <div className="mx-4 mt-2 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
          {error}
        </div>
      )}
      {draft.state === 'sent_back' && draft.note && (
        <div className="mx-4 mt-2 rounded border bg-surface-2 p-2 text-[12px]">
          <span className="font-semibold">Sent back:</span> {draft.note}
        </div>
      )}
      <main className="grid flex-1 grid-cols-1 gap-3 overflow-hidden p-3 md:grid-cols-[1fr_280px]">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          readOnly={readOnly}
          className="h-full w-full resize-none rounded border bg-canvas px-4 py-3 font-mono text-[13px] leading-relaxed outline-none focus:border-[var(--color-accent)]"
          spellCheck={false}
        />
        <aside className="flex flex-col gap-3">
          <label className="block text-[11px] text-text-muted">
            Note to reviewer
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              readOnly={readOnly}
              placeholder="Why this change…"
              className="mt-1 block h-32 w-full resize-none rounded border bg-canvas px-2 py-1 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
            />
          </label>
          <div className="rounded border bg-surface p-3 text-[11px] text-text-subtle">
            Suggested edits are reviewed by an org admin before they replace the
            team source of truth. You can keep refining and resubmitting if it
            gets sent back.
          </div>
        </aside>
      </main>
    </div>
  );
}
